import type { Message, RunStatus, RunSummary, SessionSummary } from "@herskin/contracts";

export type SessionDateGroup = "today" | "yesterday" | "earlier";

export interface SessionGroup {
  id: SessionDateGroup;
  label: string;
  sessions: SessionSummary[];
}

const BEIJING_TIME_ZONE = "Asia/Shanghai";
const DAY_IN_MILLISECONDS = 86_400_000;

const beijingTimeFormatter = new Intl.DateTimeFormat("zh-CN", {
  timeZone: BEIJING_TIME_ZONE,
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
});

const beijingDateFormatter = new Intl.DateTimeFormat("en-CA", {
  timeZone: BEIJING_TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

export function statusLabel(status: RunStatus | null) {
  if (!status) return "空闲";
  const labels: Record<RunStatus, string> = {
    submitting: "正在发送",
    accepted: "已接受",
    running: "执行中",
    waiting_approval: "等待批准",
    waiting_clarification: "等待补充",
    stopping: "正在停止",
    completed: "已完成",
    failed: "失败",
    stopped: "已停止",
    unknown: "结果待确认",
    recovering: "正在恢复",
  };
  return labels[status];
}

export function runTone(status: RunStatus | null) {
  if (!status || status === "completed") return "success" as const;
  if (["failed", "unknown"].includes(status)) return "danger" as const;
  if (["waiting_approval", "waiting_clarification", "stopping"].includes(status))
    return "warning" as const;
  if (["submitting", "accepted", "running", "recovering"].includes(status)) return "info" as const;
  return "neutral" as const;
}

export function isActivelyRunning(status: RunStatus | null | undefined) {
  return ["submitting", "accepted", "running", "recovering", "stopping"].includes(status ?? "");
}

function beijingDateKey(value: string | Date) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "invalid";
  return beijingDateFormatter.format(date);
}

export function groupSessionsByDate(sessions: SessionSummary[], now = new Date()): SessionGroup[] {
  const today = beijingDateKey(now);
  // 中国标准时间没有夏令时，减去整整 24 小时即可得到昨天的北京日期。
  const yesterday = beijingDateKey(new Date(now.getTime() - DAY_IN_MILLISECONDS));
  const groups: SessionGroup[] = [
    { id: "today", label: "今天", sessions: [] },
    { id: "yesterday", label: "昨天", sessions: [] },
    { id: "earlier", label: "更早", sessions: [] },
  ];

  for (const session of sessions) {
    const key = beijingDateKey(session.updatedAt);
    const group = key === today ? groups[0]! : key === yesterday ? groups[1]! : groups[2]!;
    group.sessions.push(session);
  }

  return groups.filter((group) => group.sessions.length > 0);
}

export function formatBeijingTime(value: string | Date) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "北京时间未知";
  return `北京时间 ${beijingTimeFormatter.format(date)}`;
}

export function formatDuration(milliseconds: number) {
  if (!Number.isFinite(milliseconds) || milliseconds < 0) return null;
  if (milliseconds < 1_000)
    return milliseconds === 0 ? "少于 1 秒" : `${Math.max(1, Math.round(milliseconds))} 毫秒`;
  if (milliseconds < 60_000) {
    const seconds = milliseconds / 1_000;
    return `${seconds < 10 ? seconds.toFixed(1) : Math.round(seconds)} 秒`;
  }
  const minutes = Math.floor(milliseconds / 60_000);
  const seconds = Math.round((milliseconds % 60_000) / 1_000);
  return seconds ? `${minutes} 分 ${seconds} 秒` : `${minutes} 分钟`;
}

export function messageTimeValue(message: Message) {
  return message.role === "assistant" && message.status !== "streaming"
    ? message.updatedAt
    : message.createdAt;
}

export interface MessagePresentation {
  message: Message;
  timeLabel: string | null;
  durationLabel: string | null;
}

function durationLabelFromStart(
  message: Message,
  roundStartedAt: string | undefined,
  activeRun: RunSummary | null,
) {
  if (message.role !== "assistant") return null;
  if (message.status === "streaming") return "生成中";
  if (message.timeAccuracy === "unknown") return null;

  const activeStart =
    message.runId && activeRun?.id === message.runId
      ? (activeRun.startedAt ?? activeRun.createdAt)
      : undefined;
  // 从 Hermes 历史水合的旧消息可能没有 run 关联，也没有真实生成起止时间。
  // 这种情况下宁可不显示耗时，也不能把“本次水合时间差”伪装成真实生成耗时。
  if (!message.runId && !roundStartedAt && !activeStart) return null;
  const start = roundStartedAt ?? activeStart ?? message.createdAt;
  const end =
    message.runId && activeRun?.id === message.runId && activeRun.completedAt
      ? activeRun.completedAt
      : message.updatedAt;
  const duration = formatDuration(new Date(end).getTime() - new Date(start).getTime());
  if (!duration) return "耗时待 Hermes 补充";
  return `${roundStartedAt || activeStart ? "耗时" : "生成耗时"} ${duration}`;
}

/**
 * Hermes 当前没有单独持久化“本轮耗时”。优先用同一 run 的用户消息到助手完成时间；
 * 老数据若缺少 run 关联与真实起止时间则不显示，避免制造虚假精度。
 */
export function assistantDurationLabel(
  message: Message,
  messages: Message[],
  activeRun: RunSummary | null,
) {
  const matchingUser = message.runId
    ? messages.find((candidate) => candidate.runId === message.runId && candidate.role === "user")
    : undefined;
  if (matchingUser?.timeAccuracy === "unknown") return null;
  return durationLabelFromStart(message, matchingUser?.createdAt, activeRun);
}

/** 为流式高频重绘准备的一次线性计算，避免每条助手消息反复扫描完整消息数组。 */
export function buildMessagePresentations(messages: Message[], activeRun: RunSummary | null) {
  const userStartByRun = new Map<string, string>();
  for (const message of messages) {
    if (message.role === "user" && message.runId && !userStartByRun.has(message.runId)) {
      userStartByRun.set(message.runId, message.createdAt);
    }
  }
  return messages.map<MessagePresentation>((message) => ({
    message,
    timeLabel:
      message.timeAccuracy === "unknown" ? null : formatBeijingTime(messageTimeValue(message)),
    durationLabel:
      message.timeAccuracy === "unknown"
        ? null
        : durationLabelFromStart(
            message,
            message.runId ? userStartByRun.get(message.runId) : undefined,
            activeRun,
          ),
  }));
}
